/**
 * Position-specific curated playlists for soccer.
 * Each position path references existing lesson IDs from soccerLessons.ts
 * and adds a description of why that lesson is relevant for the position.
 */

export interface PositionPath {
  id: string;
  position: string;
  emoji: string;
  shortDescription: string;
  description: string;
  /** Ordered list of existing lesson IDs that are most relevant */
  lessonIds: string[];
  /** Why each lesson matters for this position */
  lessonRelevance: Record<string, string>;
}

export const soccerPositionPaths: PositionPath[] = [
  {
    id: 'pos-goalkeeper',
    position: 'Goalkeeper',
    emoji: '🧤',
    shortDescription: 'The last line of defense',
    description: 'Learn what makes a modern goalkeeper — from shot-stopping to sweeper-keeper duties and building from the back.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-goalkeeper',
      'soccer-pressing-basics',
      'soccer-corners-fks',
      'soccer-penalty-kicks',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'Understand how formations shape your defensive setup and positioning.',
      'soccer-goalkeeper': 'Core lesson on GK roles, sweeper-keeper concepts, and modern goalkeeper demands.',
      'soccer-pressing-basics': 'Learn when to hold your line vs. sweep behind a high defensive line.',
      'soccer-corners-fks': 'Crucial for commanding your box — organizing the wall and defending set pieces.',
      'soccer-penalty-kicks': 'Master the mental game and technique of penalty saves and shootouts.',
    },
  },
  {
    id: 'pos-center-back',
    position: 'Center-Back',
    emoji: '🛡️',
    shortDescription: 'The defensive anchor',
    description: 'Master positioning, aerial duels, and building play from the back as a modern center-back.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-442',
      'soccer-goalkeeper',
      'soccer-pressing-basics',
      'soccer-gegenpressing',
      'soccer-corners-fks',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'Understand how different formations affect your defensive partnerships.',
      'soccer-442': 'Learn the classic flat back four and your role in a 2-CB pairing.',
      'soccer-goalkeeper': 'Understand the defensive unit — how CBs coordinate with the GK and full-backs.',
      'soccer-pressing-basics': 'Know when to hold a high line vs. drop into a low block.',
      'soccer-gegenpressing': 'Learn how to support the counter-press and win second balls.',
      'soccer-corners-fks': 'Defending and scoring from set pieces is a huge part of the CB role.',
    },
  },
  {
    id: 'pos-full-back',
    position: 'Full-Back / Wing-Back',
    emoji: '🏃',
    shortDescription: 'Attack & defend the flanks',
    description: 'Modern full-backs are hybrid players — learn to overlap, defend 1v1, and deliver from wide areas.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-442',
      'soccer-433',
      'soccer-goalkeeper',
      'soccer-pressing-basics',
      'soccer-corners-fks',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'See how your role changes between a back 4 vs. a back 3 with wing-backs.',
      'soccer-442': 'Understand the classic full-back role in a flat 4-4-2.',
      'soccer-433': 'In a 4-3-3, full-backs often provide the width while wingers cut inside.',
      'soccer-goalkeeper': 'Learn how full-backs fit into the defensive unit and coordinate with CBs.',
      'soccer-pressing-basics': 'Full-backs must press high in a high press but recover in a low block.',
      'soccer-corners-fks': 'Often deliver corners and free kicks — or mark at the near/far post.',
    },
  },
  {
    id: 'pos-defensive-mid',
    position: 'Defensive Midfielder',
    emoji: '⚙️',
    shortDescription: 'The shield & metronome',
    description: 'Control the game from deep — shield the back line, recycle possession, and dictate tempo.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-433',
      'soccer-midfield',
      'soccer-pressing-basics',
      'soccer-gegenpressing',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'The DM role varies hugely between formations — single vs. double pivot.',
      'soccer-433': 'In a 4-3-3, the DM is the "pivot" at the base of the midfield triangle.',
      'soccer-midfield': 'Core lesson on DM responsibilities, the destroyer vs. controller archetype.',
      'soccer-pressing-basics': 'You set the pressing line — know when to press and when to hold shape.',
      'soccer-gegenpressing': 'The DM is key to counter-pressing — winning second balls and cutting passing lanes.',
    },
  },
  {
    id: 'pos-central-mid',
    position: 'Central Midfielder',
    emoji: '🔄',
    shortDescription: 'The box-to-box engine',
    description: 'Cover every blade of grass — tackle, pass, create, and score as the team\'s engine.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-442',
      'soccer-433',
      'soccer-midfield',
      'soccer-pressing-basics',
      'soccer-gegenpressing',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'Your positioning depends heavily on the formation and midfield shape.',
      'soccer-442': 'In a 4-4-2 you play as part of a midfield pair — more defensive responsibility.',
      'soccer-433': 'In a 4-3-3 you have more freedom as one of the two advanced midfielders.',
      'soccer-midfield': 'Core lesson on CM roles — box-to-box, playmaker, and creative duties.',
      'soccer-pressing-basics': 'CMs are the engine of any press — high or low.',
      'soccer-gegenpressing': 'Winning the ball back immediately is a core CM responsibility.',
    },
  },
  {
    id: 'pos-attacking-mid',
    position: 'Attacking Midfielder (#10)',
    emoji: '🎩',
    shortDescription: 'The creative playmaker',
    description: 'Unlock defenses with vision, through balls, and goals from the #10 position.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-433',
      'soccer-midfield',
      'soccer-pressing-basics',
      'soccer-corners-fks',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'The #10 role only exists in certain formations — learn which ones.',
      'soccer-433': 'Some 4-3-3 variants use an advanced #10 instead of a traditional winger.',
      'soccer-midfield': 'Core lesson on the #10 role, playmaking, and the creative hub.',
      'soccer-pressing-basics': 'As a #10, you\'re often the first presser — triggering the press from the front.',
      'soccer-corners-fks': 'Many #10s are set piece specialists — free kicks and corner delivery.',
    },
  },
  {
    id: 'pos-winger',
    position: 'Winger',
    emoji: '⚡',
    shortDescription: 'Speed, skill & end product',
    description: 'Beat defenders, deliver crosses, and cut inside to score from wide positions.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-433',
      'soccer-midfield',
      'soccer-pressing-basics',
      'soccer-gegenpressing',
      'soccer-corners-fks',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'Wingers are central to 4-3-3 and 4-2-3-1 systems.',
      'soccer-433': 'The 4-3-3 is THE winger formation — learn how LW and RW operate.',
      'soccer-midfield': 'Understand how wingers connect with midfielders and full-backs.',
      'soccer-pressing-basics': 'Wingers are often the first line of defense in a high press.',
      'soccer-gegenpressing': 'Immediate pressing after losing the ball is key for wide players.',
      'soccer-corners-fks': 'Wingers often deliver corners and free kicks from wide areas.',
    },
  },
  {
    id: 'pos-striker',
    position: 'Striker / Center Forward',
    emoji: '🎯',
    shortDescription: 'The goalscorer',
    description: 'Learn movement, finishing, and link-up play as the team\'s focal point in attack.',
    lessonIds: [
      'soccer-what-is-formation',
      'soccer-442',
      'soccer-433',
      'soccer-pressing-basics',
      'soccer-gegenpressing',
      'soccer-penalty-kicks',
    ],
    lessonRelevance: {
      'soccer-what-is-formation': 'Your role changes between being a lone 9 vs. a strike partnership.',
      'soccer-442': 'The 4-4-2 uses two strikers — learn how strike partnerships work.',
      'soccer-433': 'In a 4-3-3, you\'re the lone striker — hold-up play becomes crucial.',
      'soccer-pressing-basics': 'Strikers initiate the press — learn pressing triggers from the front.',
      'soccer-gegenpressing': 'Pressing immediately after losing the ball near the opponent\'s goal creates chances.',
      'soccer-penalty-kicks': 'Strikers are often on penalty duty — master the techniques.',
    },
  },
];
